export type PriceType = "sale" | "rent" | "event" | "hotel";

export type RentFrequency = "year" | "month";

export const priceLabels = {
  sale: "Asking Price",
  rent: "Per Year",
  event: "Per Event",
  hotel: "Per Night",
} as const;

const LEGACY_RENT_YEAR = "per-year";
const LEGACY_RENT_MONTH = "per-month";
const LEGACY_EVENT = "per-event";
const LEGACY_HOTEL = "per-night";

export function formatNaira(amount: number): string {
  return `₦${amount.toLocaleString("en-NG")}`;
}

export function formatPrice(amount: number | null | undefined, priceText?: string): string {
  if (typeof amount === "number" && Number.isFinite(amount)) {
    return formatNaira(amount);
  }

  return priceText ?? "Contact for pricing";
}

export function getPriceLabel(priceType: PriceType, rentFrequency: RentFrequency = "year"): string {
  if (priceType === "rent") {
    return rentFrequency === "month" ? "Per Month" : "Per Year";
  }

  return priceLabels[priceType];
}

export function normalizePriceType(
  raw: string | null | undefined,
  fallback: PriceType = "sale",
): { priceType: PriceType; rentFrequency?: RentFrequency } {
  if (!raw) {
    return { priceType: fallback };
  }

  const normalized = raw.toLowerCase().trim();

  if (normalized === "sale") return { priceType: "sale" };
  if (normalized === "rent") return { priceType: "rent", rentFrequency: "year" };
  if (normalized === "event") return { priceType: "event" };
  if (normalized === "hotel") return { priceType: "hotel" };

  if (normalized === LEGACY_RENT_YEAR) return { priceType: "rent", rentFrequency: "year" };
  if (normalized === LEGACY_RENT_MONTH) return { priceType: "rent", rentFrequency: "month" };
  if (normalized === LEGACY_EVENT) return { priceType: "event" };
  if (normalized === LEGACY_HOTEL) return { priceType: "hotel" };

  return { priceType: fallback };
}

export function parsePriceValue(raw: string | number | null | undefined): { amount: number | null; priceText?: string } {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return { amount: raw };
  }

  if (typeof raw !== "string") {
    return { amount: null };
  }

  const trimmed = raw.trim();

  if (!trimmed) {
    return { amount: null };
  }

  if (trimmed.toLowerCase() === "free") {
    return { amount: 0 };
  }

  const numeric = Number(trimmed.replace(/[^\d.-]/g, ""));
  if (Number.isFinite(numeric) && numeric > 0) {
    return { amount: numeric };
  }

  return { amount: null, priceText: trimmed };
}